import { createContext, useCallback, useContext, useState } from "react";
import type { DocumentType, BoundingBox, FieldSchema } from "@/features/schema-management/types";
import { Type, Calendar, Table, Binary, ToggleRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface DocumentTypeField {
    id: string;
    label: string;
    value: string;
    confidenceLevel: number | null;
    fieldType: string;
    description: string;
    boundingBoxes: BoundingBox[];
    columnNames?: string[];
    columnTypes?: string[];
    rows?: unknown[][];
    originalValue?: unknown;
}

export interface DocumentTypeView {
    id: number;
    name: string;
    mimeType: string;
    documentType: string;
    confidenceThreshold: number;
    fields: DocumentTypeField[];
}

export const FIELD_TYPES = [
    "Text",
    "Date",
    "Table",
    "Number",
    "Yes / No",
] as const;

export const FIELD_TYPE_ICONS: Record<string, LucideIcon> = {
    "Text": Type,
    "Date": Calendar,
    "Table": Table,
    "Number": Binary,
    "Yes / No": ToggleRight,
};

const BACKEND_TO_FIELD_TYPE: Record<string, string> = {
    "str": "Text",
    "Optional[str]": "Text",
    "number": "Number",
    "Optional[number]": "Number",
    "bool": "Yes / No",
    "Optional[bool]": "Yes / No",
};


function toCamelCase(str: string): string {
    return str.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase());
}

function primaryBox(boxes: BoundingBox[]): BoundingBox | undefined {
    if (boxes.length === 0) return undefined;
    return boxes.reduce((best, box) => {
        if (box.pageN !== best.pageN) return box.pageN < best.pageN ? box : best;
        if (box.top !== best.top) return box.top < best.top ? box : best;
        return box.left < best.left ? box : best;
    });
}

export function sortFieldsByPosition(fields: DocumentTypeField[]): DocumentTypeField[] {
    return [...fields].sort((a, b) => {
        const aBox = primaryBox(a.boundingBoxes);
        const bBox = primaryBox(b.boundingBoxes);
        if (!aBox && !bBox) return 0;
        if (!aBox) return 1;
        if (!bBox) return -1;
        if (aBox.pageN !== bBox.pageN) return aBox.pageN - bBox.pageN;
        if (aBox.top !== bBox.top) return aBox.top - bBox.top;
        return aBox.left - bBox.left;
    });
}

export function toSnakeCase(str: string): string {
    return str.trim().toLowerCase().replace(/[\s\-\.]+/g, '_').replace(/[^a-z0-9_]/g, '');
}

export function toDisplayName(snakeCase: string): string {
    return snakeCase.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function buildFieldsFromExtractedData(
    properties: FieldSchema[],
    extractedData: Record<string, unknown>
): DocumentTypeField[] {
    const fields = properties.map((prop) => {
        const camelKey = toCamelCase(prop.fieldName);
        const isTable = prop.columnNames != null;

        if (isTable) {
            const fieldData = extractedData[camelKey] as Record<string, unknown> | undefined;
            const rawRows = fieldData?.rows as unknown[][] | undefined;
            const ref = extractedData[`${camelKey}Reference`] as Record<string, unknown> | undefined;
            const confidence = ref?.confidenceScore as number | undefined;

            return {
                id: prop.fieldName,
                label: prop.fieldName,
                value: rawRows != null ? "Table Content" : "",
                confidenceLevel: confidence != null ? Math.round(confidence) : null,
                fieldType: "Table",
                description: prop.description,
                boundingBoxes: (ref?.boundingBoxes as BoundingBox[] | undefined) ?? [],
                columnNames: prop.columnNames,
                columnTypes: prop.columnTypes,
                rows: rawRows,
                originalValue: ref?.originalValue,
            };
        }

        const value = extractedData[camelKey];
        const ref = extractedData[`${camelKey}Reference`] as Record<string, unknown> | undefined;
        const confidence = ref?.confidenceScore as number | undefined;

        return {
            id: prop.fieldName,
            label: prop.fieldName,
            value: value != null ? String(value) : "",
            confidenceLevel: confidence != null ? Math.round(confidence) : null,
            fieldType: BACKEND_TO_FIELD_TYPE[prop.fieldType ?? ""] ?? "Text",
            description: prop.description,
            boundingBoxes: (ref?.boundingBoxes as BoundingBox[] | undefined) ?? [],
            originalValue: ref?.originalValue,
        };
    });
    return sortFieldsByPosition(fields);
}

export function toDocumentTypeView(doc: DocumentType): DocumentTypeView {
    const extractedData = doc.metadata?.extractedDocumentData ?? {};
    const label = doc.documentType ?? "";

    return {
        id: doc.id,
        name: doc.fileName,
        mimeType: doc.mimeType,
        documentType: label,
        confidenceThreshold: doc.confidenceLevelThreshold,
        fields: buildFieldsFromExtractedData(doc.properties ?? [], extractedData),
    };
}


interface SchemaEditContextValue {
    documents: DocumentTypeView[];
    setDocuments: React.Dispatch<React.SetStateAction<DocumentTypeView[]>>;
    selectedDocIndex: number;
    setSelectedDocIndex: (index: number) => void;
    completedDocIds: Set<number>;
    markDocComplete: (id: number) => void;
    resetCompletedDocIds: () => void;
}

const SchemaEditContext = createContext<SchemaEditContextValue | null>(null);

export function SchemaEditProvider({ children }: { children: React.ReactNode }) {
    const [documents, setDocuments] = useState<DocumentTypeView[]>([]);
    const [selectedDocIndex, setSelectedDocIndex] = useState(0);
    const [completedDocIds, setCompletedDocIds] = useState<Set<number>>(new Set());

    const markDocComplete = (id: number) => {
        setCompletedDocIds((prev) => new Set(prev).add(id));
    };

    const resetCompletedDocIds = useCallback(() => {
        setCompletedDocIds(new Set());
    }, []);

    return (
        <SchemaEditContext.Provider value={{ documents, setDocuments, selectedDocIndex, setSelectedDocIndex, completedDocIds, markDocComplete, resetCompletedDocIds }}>
            {children}
        </SchemaEditContext.Provider>
    );
}

export function useSchemaEdit() {
    const ctx = useContext(SchemaEditContext);
    if (!ctx) throw new Error("useSchemaEdit must be used within SchemaEditProvider");
    return ctx;
}

export function useSchemaEditSafe() {
    return useContext(SchemaEditContext);
}
